/**
 * API-Client fuer den kombinierten Tabellen-Vertrag aus 39-08 plus die Sperr- und
 * Uebertragungsendpunkte aus 39-04/39-07 (siehe `39-10-PLAN.md`, Abschnitt `<interfaces>`).
 *
 * Bewusst kein Marktdaten-Praefix in Datei- oder Objektname: Umwelt- und Unfallauswertungen
 * sollen spaeter dieselbe Seitenhuelle (Periodenleiste, Statusleiste, Uebertragung) nutzen und
 * nur eine eigene Tabellenart (`layout`) mitbringen -- `evaluationType` bleibt aber weiterhin auf
 * die fuenf Marktauswertungen beschraenkt, keine Vorwegnahme darueber hinaus.
 *
 * Nutzt ausschliesslich den gemeinsamen `api`-Client (`services/api.ts`), keine eigene
 * axios-Instanz.
 */
import { api } from './api';
import type { EvaluationType } from '../types/evaluations';
import type {
  BreakdownResponse,
  CellDecisionPayload,
  CellDecisionResponse,
  DeleteCellDecisionParams,
  EvaluationOverviewResponse,
  EvaluationTableResponse,
  TransferPreview,
  TransferResult,
} from '../types/evaluationTable';

export interface TablePeriod {
  year: number;
  period: number;
}

async function getTable(
  evaluationType: EvaluationType,
  period?: TablePeriod
): Promise<EvaluationTableResponse> {
  const params: Record<string, string | number> = { evaluationType };
  if (period) {
    params.year = period.year;
    params.period = period.period;
  }
  const response = await api.get<EvaluationTableResponse>(
    '/api/evaluations/table',
    { params }
  );
  return response.data;
}

async function getOverview(): Promise<EvaluationOverviewResponse> {
  const response = await api.get<EvaluationOverviewResponse>(
    '/api/evaluations/overview'
  );
  return response.data;
}

async function getBreakdown(
  evaluationType: EvaluationType,
  year: number,
  period: number,
  rowKey: string
): Promise<BreakdownResponse> {
  const response = await api.get<BreakdownResponse>(
    '/api/evaluations/breakdown',
    {
      params: { evaluationType, year, period, rowKey },
    }
  );
  return response.data;
}

async function setCellDecision(
  payload: CellDecisionPayload
): Promise<CellDecisionResponse> {
  const response = await api.put<CellDecisionResponse>(
    '/api/evaluations/cell-decisions',
    payload
  );
  return response.data;
}

async function deleteCellDecision(
  params: DeleteCellDecisionParams
): Promise<void> {
  await api.delete('/api/evaluations/cell-decisions', { params });
}

async function transfer(
  evaluationType: EvaluationType,
  year: number,
  period: number
): Promise<TransferResult> {
  const response = await api.post<TransferResult>(
    '/api/statistik-portal/export',
    {
      evaluationType,
      year,
      period,
    }
  );
  return response.data;
}

async function getTransferPreview(
  evaluationType: EvaluationType,
  year: number,
  period: number
): Promise<TransferPreview> {
  const response = await api.get<TransferPreview>(
    '/api/statistik-portal/preview',
    {
      params: { evaluationType, year, period },
    }
  );
  return response.data;
}

/** Dateiname-Rueckfall, falls der Server keinen `content-disposition`-Header liefert. */
function fallbackCsvFilename(
  evaluationType: EvaluationType,
  year: number,
  period: number
): string {
  return `${evaluationType}_${year}_${period}.csv`;
}

function filenameFromContentDisposition(
  header: string | undefined
): string | null {
  if (!header) {
    return null;
  }
  const match = /filename="?([^";]+)"?/.exec(header);
  return match ? match[1] : null;
}

async function downloadCsv(
  evaluationType: EvaluationType,
  year: number,
  period: number
): Promise<{ blob: Blob; filename: string }> {
  const response = await api.get<Blob>('/api/statistik-portal/csv', {
    params: { evaluationType, year, period },
    responseType: 'blob',
  });
  const filename =
    filenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined
    ) ?? fallbackCsvFilename(evaluationType, year, period);
  return { blob: response.data, filename };
}

export const evaluationTableApi = {
  getTable,
  getOverview,
  getBreakdown,
  setCellDecision,
  deleteCellDecision,
  transfer,
  getTransferPreview,
  downloadCsv,
};
